import { type User as CoreUser } from '@grammarly/http-clients-auth-v5'

import { getLogger } from '@lib/tracking/felog/telemetry'

const logger = getLogger('user.clientFallback')

/**
 * Response from the mediator token API
 */
interface TokenResponse {
  token: string
  expiresAt?: string
}

/**
 * Fetches the current user by following the same flow as the proxy server:
 * 1. Call `/v1/mediator/token` to get an access token
 * 2. Call `/auth/v5/api/userinfo` with Bearer token to get user data
 *
 * Returns CoreUser data if authenticated, null if not authenticated.
 */
export async function fetchUserWithToken(): Promise<CoreUser | null> {
  try {
    // Step 1: Get access token from mediator
    const tokenResponse = await fetch('/v1/mediator/token', {
      method: 'GET',
      credentials: 'include', // Include cookies for authentication
      headers: {
        Accept: 'application/json',
      },
    })

    if (tokenResponse.status === 401 || !tokenResponse.ok) {
      // No valid token, user is not authenticated
      return null
    }

    const tokenData: TokenResponse = await tokenResponse.json()

    if (!tokenData.token) {
      return null
    }

    // Step 2: Get user info using the Bearer token
    const userInfoResponse = await fetch('/auth/v5/api/userinfo', {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${tokenData.token}`,
      },
    })

    if (userInfoResponse.status === 401 || !userInfoResponse.ok) {
      // Token is invalid or expired
      return null
    }

    const userData: CoreUser = await userInfoResponse.json()
    return userData
  } catch (error) {
    logger.error('Error fetching user with token:', error)
    // Return null on error to treat as unauthenticated
    return null
  }
}
